/**
 * SessionTimeoutModal.jsx — Warns user before auto-logout.
 *
 * Shows when the user has been idle for (idleTimeout - warningTimeout).
 * Displays a live countdown and two options:
 *   "Stay Signed In" — resets the idle timer
 *   "Log Out Now"    — logs out immediately
 *
 * Props:
 *   visible         {boolean}  - whether to render the modal
 *   secondsLeft     {number}   - countdown seconds remaining
 *   onStay          {function} - user clicked "Stay Signed In"
 *   onLogout        {function} - user clicked "Log Out Now"
 */

import React from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
import './SessionTimeoutModal.css';

export default function SessionTimeoutModal({ visible, secondsLeft, onStay, onLogout }) {
  if (!visible) return null;

  // Format seconds as m:ss
  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');
  const timeStr = mins > 0 ? `${mins}:${secs}` : `0:${secs}`;

  // Turn countdown red when under 30 seconds
  const urgent = secondsLeft <= 30;

  return (
    // Overlay covers entire viewport; clicking outside is intentionally blocked
    <div className="stm-overlay" role="dialog" aria-modal="true" aria-labelledby="stm-title">
      <div className="stm-card">
        {/* Icon */}
        <div className={`stm-icon ${urgent ? 'stm-icon--urgent' : ''}`}>
          <Clock size={28} />
        </div>

        {/* Title */}
        <h2 className="stm-title" id="stm-title">
          Session expiring soon
        </h2>

        {/* Message */}
        <p className="stm-body">
          You've been inactive. For security, your session will end in:
        </p>

        {/* Countdown */}
        <div className={`stm-countdown ${urgent ? 'stm-countdown--urgent' : ''}`}>
          {timeStr}
        </div>

        {/* Actions */}
        <div className="stm-actions">
          <button className="stm-btn stm-btn--primary" onClick={onStay} autoFocus>
            <RefreshCw size={15} />
            Stay Signed In
          </button>
          <button className="stm-btn stm-btn--ghost" onClick={onLogout}>
            <LogOut size={15} />
            Log Out Now
          </button>
        </div>
      </div>
    </div>
  );
}
